import { MOIS_FR } from "@/lib/api";

const sansAccent = (s) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
const MOIS = MOIS_FR.map((m) => sansAccent(m).replace(".", ""));
const NOMBRE = /^[-−+]?\s*[\d\s\u00a0\u202f]*\d(?:[.,]\d+)?\s*(?:\$|%)?$/;

export const colonnes = (t) => {
  const h = t?.tHead;
  return h && h.rows.length ? [...h.rows[h.rows.length - 1].cells] : [];
};

export const cles = (t) => {
  const vus = new Set();
  return colonnes(t).map((th, i) => {
    let k = th.textContent.trim() || `#${i}`;
    if (vus.has(k)) k = `${k}#${i}`;
    vus.add(k);
    return k;
  });
};

const visible = (el) => el.getClientRects().length > 0;

export const largeursActuelles = (t) => {
  const ks = cles(t);
  return Object.fromEntries(colonnes(t).map((th, i) => [ks[i], th.offsetWidth]).filter(([, w]) => w > 0));
};

const reinitialiser = (t, ths) => {
  t.style.tableLayout = "";
  t.style.width = "";
  t.style.minWidth = "";
  t.classList.remove("st-fixe");
  ths.forEach((th) => { th.style.width = ""; });
};

export const appliquerLargeurs = (t, w) => {
  const ths = colonnes(t);
  const ks = cles(t);
  if (!Object.keys(w).length) { reinitialiser(t, ths); return; }
  const vis = ths.map(visible);
  if (ths.some((_, i) => vis[i] && !(ks[i] in w))) reinitialiser(t, ths);
  const px = ths.map((th, i) => (ks[i] in w ? w[ks[i]] : th.offsetWidth));
  ths.forEach((th, i) => { th.style.width = `${px[i]}px`; });
  t.style.tableLayout = "fixed";
  t.style.minWidth = "0";
  t.style.width = `${px.reduce((s, x, i) => s + (vis[i] ? x : 0), 0)}px`;
  t.classList.add("st-fixe");
};

export const largeurAuto = (t, i) => {
  const ths = colonnes(t);
  const style = t.getAttribute("style") || "";
  const ancienne = ths[i].style.width;
  t.style.tableLayout = "auto";
  t.style.width = "max-content";
  t.style.minWidth = "0";
  ths[i].style.width = "";
  t.classList.add("st-mesure");
  const px = ths[i].offsetWidth;
  t.classList.remove("st-mesure");
  t.setAttribute("style", style);
  ths[i].style.width = ancienne;
  return Math.max(40, px + 4);
};

export const valeurCellule = (td) => {
  if (!td) return "";
  if (td.dataset.stValeur != null) return td.dataset.stValeur;
  const champ = td.querySelector("input:not([type=checkbox]), select, textarea");
  return (champ ? champ.value : td.textContent).trim();
};

const cleTri = (t) => {
  if (NOMBRE.test(t)) return { n: Number(t.replace(/[^\d,.\-−]/g, "").replace("−", "-").replace(",", ".")) };
  const m = sansAccent(t).match(/^([a-z]+)\.?\s+(\d{4})$/);
  if (m && MOIS.includes(m[1])) return { n: Number(m[2]) * 12 + MOIS.indexOf(m[1]) };
  return { s: t };
};

const MILLIERS = /^[-−+]?\d{1,3}(?:[\s\u00a0\u202f]\d{3})*(?:[.,]\d+)?\s*(?:\$|%)?$/;
const SIMPLE = /^[-−+]?(\d+)(?:[.,]\d+)?\s*(\$|%)?$/;

const estNombre = (t) => {
  const m = t.match(SIMPLE);
  if (m) return !!m[2] || /[.,]/.test(t) || (m[1].length <= 7 && !(m[1].length > 1 && m[1][0] === "0"));
  return MILLIERS.test(t);
};

export const celluleExcel = (t) => {
  if (!t) return { v: null };
  if (!estNombre(t)) return { v: t };
  const n = Number(t.replace(/[^\d,.\-−]/g, "").replace("−", "-").replace(",", "."));
  if (t.includes("$")) return { v: n, f: "money" };
  if (t.includes("%")) return { v: n / 100, f: "pct" };
  return { v: n, f: Number.isInteger(n) ? "int" : "num" };
};

const TRANSPARENT = new Set(["inline", "contents"]);
const bloc = (n, racine) => {
  let e = n.parentElement;
  while (e && e !== racine && TRANSPARENT.has(getComputedStyle(e).display)) e = e.parentElement;
  return e;
};

const texteExport = (el) => {
  const marcheur = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  const nodes = [];
  while (marcheur.nextNode()) if (marcheur.currentNode.nodeValue) nodes.push(marcheur.currentNode);
  const horsBoutons = nodes.filter((n) => !n.parentElement.closest("button"));
  const garde = horsBoutons.some((n) => n.nodeValue.trim()) ? horsBoutons : nodes;
  const blocs = garde.map((n) => bloc(n, el));
  return garde.reduce((s, n, i) => s + (i && blocs[i - 1] !== blocs[i] ? " " : "") + n.nodeValue, "").replace(/\s+/g, " ").trim();
};

const valeurExport = (td) => {
  if (td.dataset.stExport != null) return td.dataset.stExport;
  const champ = td.querySelector("input:not([type=checkbox]), select, textarea");
  return champ ? champ.value.trim() : texteExport(td);
};

export const extraireTable = (t, { sansActions = false } = {}) => {
  const ths = colonnes(t);
  const vis = ths.map((th, i) => (visible(th) && !(sansActions && th.dataset.stExport === "non") ? i : -1)).filter((i) => i >= 0);
  const lignes = [...t.tBodies, ...(t.tFoot ? [t.tFoot] : [])].flatMap((tb) => [...tb.rows]).map((tr) => (
    tr.cells.length === ths.length
      ? vis.map((i) => celluleExcel(valeurExport(tr.cells[i])))
      : [celluleExcel(texteExport(tr))]
  ));
  return { colonnes: vis.map((i) => ths[i].textContent.trim()), lignes };
};

export const donneesExport = (t, opts) => (t.stExport ? t.stExport(t) : extraireTable(t, opts));

export const comparer = (a, b) => {
  const x = cleTri(a);
  const y = cleTri(b);
  if ("n" in x && "n" in y) return x.n - y.n;
  if ("n" in x) return -1;
  if ("n" in y) return 1;
  return x.s.localeCompare(y.s, "fr", { numeric: true, sensitivity: "base" });
};

const grouper = (tb, nb) => {
  const groupes = [];
  const fixes = [];
  const enfants = [];
  [...tb.rows].forEach((tr) => {
    if (tr.dataset.stFixe != null) fixes.push(tr);
    else if (tr.dataset.stParent != null) enfants.push(tr);
    else if (tr.cells.length < nb && groupes.length) groupes[groupes.length - 1].push(tr);
    else groupes.push([tr]);
  });
  enfants.forEach((tr) => {
    const g = groupes.find((x) => x[0].dataset.stId === tr.dataset.stParent);
    if (g) g.push(tr); else groupes.push([tr]);
  });
  return { groupes, fixes };
};

const estAction = (td) => !!td.querySelector("button, a, [role=combobox]");

export const colonneTriable = (t, i) => {
  const nb = colonnes(t).length;
  const cellules = [...t.tBodies].flatMap((tb) => [...tb.rows]).filter((tr) => tr.cells.length === nb).map((tr) => tr.cells[i]);
  const pleines = cellules.filter((td) => td.textContent.trim() || td.querySelector("input, select, button, a"));
  return !pleines.length || !pleines.every(estAction);
};

export const trierTable = (t, i, sens) => {
  const nb = colonnes(t).length;
  [...t.tBodies].forEach((tb) => {
    const { groupes, fixes } = grouper(tb, nb);
    const v = (g) => (g[0].cells.length < nb ? "" : valeurCellule(g[0].cells[i]));
    groupes.sort((a, b) => {
      const va = v(a);
      const vb = v(b);
      if (!va || !vb) return (!va) - (!vb);
      return sens * comparer(va, vb);
    });
    const voulu = [...groupes.flat(), ...fixes];
    if (voulu.every((tr, k) => tb.rows[k] === tr)) return;
    voulu.forEach((tr) => tb.appendChild(tr));
  });
};
